import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// Types
export interface InsuranceFloaterMember {
  id: number;
  dependant?: number | null;
  dependant_data?: { id: number; name: string; relationship?: string } | null;
  is_self: boolean;
  uhid?: string;
}

export interface InsurancePolicyDocument {
  id: number;
  file: string;
  file_name?: string;
  created_at: string;
}

export interface InsuranceCompanyOption {
  id: number;
  name: string;
  insurance_type: string;
}

export type InsuranceType =
  | 'health'
  | 'life'
  | 'motor'
  | 'personal_accident'
  | 'critical_illness'
  | 'travel'
  | 'home_property'
  | 'other';

export interface InsurancePolicy {
  id: number;
  policy_owner_type: 'self' | 'dependant' | 'company';
  plan_type: 'individual' | 'floater';
  for_whom: 'self' | 'dependant';
  dependant?: number | null;
  dependant_data?: { id: number; name: string; relationship?: string } | null;
  is_self_included?: boolean;
  policy_holder_name?: string;
  policy_from: string;
  policy_to: string;
  type_of_insurance: InsuranceType;
  insurance_company: string;
  policy_number: string;
  policy_name?: string;
  sum_assured?: number;
  premium_amount?: number;
  tpa?: string;
  nominee?: string;
  type_specific_data?: Record<string, any>;
  renewal_reminder_enabled?: boolean;
  renewal_frequency?: 'one_time' | 'recurring' | null;
  renewal_reminder_type?: 'days' | 'weeks' | 'months' | null;
  renewal_reminder_value?: number;
  renewal_acknowledged_at?: string | null;
  group_policy_details?: string;
  policy_features?: string;
  notes?: string;
  floater_members: InsuranceFloaterMember[];
  documents: InsurancePolicyDocument[];
  created_at: string;
  updated_at: string;
}

export interface InsurancePolicyPayload {
  policy_owner_type: 'self' | 'dependant' | 'company';
  plan_type: 'individual' | 'floater';
  is_self_included?: boolean;
  dependant?: number;
  policy_from: string;
  policy_to: string;
  type_of_insurance: string;
  insurance_company: string;
  policy_number: string;
  policy_name?: string;
  sum_assured?: number;
  premium_amount?: number;
  tpa?: string;
  nominee?: string;
  type_specific_data?: Record<string, any>;
  renewal_reminder_enabled?: boolean;
  renewal_frequency?: string;
  renewal_reminder_type?: string;
  renewal_reminder_value?: number;
  group_policy_details?: string;
  policy_features?: string;
  notes?: string;
  self_uhid?: string;
  floater_members?: { dependant_id: number; uhid?: string }[];
}

export interface InsuranceChoices {
  policy_owner_type: Record<string, string>;
  plan_type: Record<string, string>;
  type_of_insurance: Record<string, string>;
  renewal_frequency: Record<string, string>;
  renewal_reminder_type: Record<string, string>;
}

export interface FieldChoiceOption {
  value: string;
  label: string;
}

// { motor: { vehicle_type: [...], motor_cover_type: [...] }, life: { life_policy_type: [...] }, ... }
export type InsuranceFieldChoicesMap = Record<
  string,
  Record<string, FieldChoiceOption[]>
>;

export interface ExtractedInsuranceData {
  type_of_insurance: string | null;
  insurance_company: string | null;
  policy_number: string | null;
  policy_name: string | null;
  policy_holder_name: string | null;
  policy_from: string | null;
  policy_to: string | null;
  sum_assured: number | null;
  premium_amount: number | null;
  tpa: string | null;
  nominee: string | null;
  type_specific_data: Record<string, any>;
}

const insuranceService = {
  // Get all policies
  getPolicies: async (params?: {
    policy_owner_type?: string;
    who?: 'self' | 'dependant';
    start?: string;
    end?: string;
    company?: string;
  }): Promise<InsurancePolicy[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.insurance.policies, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  // Get single policy
  getPolicy: async (id: number): Promise<InsurancePolicy> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.insurance.policies}${id}/`);
    return response.data.data || response.data;
  },

  // Create policy
  createPolicy: async (data: InsurancePolicyPayload, documents?: File[]): Promise<InsurancePolicy> => {
    if (documents && documents.length > 0) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      documents.forEach(file => formData.append('documents', file));
      const response = await axiosInstance.post(API_ENDPOINTS.insurance.policies, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    }
    const response = await axiosInstance.post(API_ENDPOINTS.insurance.policies, data);
    return response.data.data;
  },

  // Update policy
  updatePolicy: async (id: number, data: InsurancePolicyPayload, documents?: File[]): Promise<InsurancePolicy> => {
    if (documents && documents.length > 0) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      documents.forEach(file => formData.append('documents', file));
      const response = await axiosInstance.put(`${API_ENDPOINTS.insurance.policies}${id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    }
    const response = await axiosInstance.put(`${API_ENDPOINTS.insurance.policies}${id}/`, data);
    return response.data.data;
  },

  // Delete policy (soft delete)
  deletePolicy: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.insurance.policies}${id}/`);
  },

  // Acknowledge renewal reminder — stops recurring reminders for this cycle
  acknowledgeRenewal: async (id: number): Promise<InsurancePolicy> => {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.insurance.policies}${id}/acknowledge-renewal/`
    );
    return response.data.data;
  },

  // Upload documents to existing policy
  addDocuments: async (policyId: number, files: File[]): Promise<InsurancePolicyDocument[]> => {
    const formData = new FormData();
    files.forEach(file => formData.append('documents', file));
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.insurance.policies}${policyId}/documents/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  // Delete document
  deleteDocument: async (policyId: number, documentId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.insurance.policies}${policyId}/documents/delete/`, {
      data: { document_id: documentId }
    });
  },

  // Get choices for dropdowns
  getChoices: async (): Promise<InsuranceChoices> => {
    const response = await axiosInstance.get(API_ENDPOINTS.insurance.choices);
    return response.data;
  },

  // Get enum options for type_specific_data fields (per insurance type)
  getFieldChoices: async (): Promise<InsuranceFieldChoicesMap> => {
    const response = await axiosInstance.get(API_ENDPOINTS.insurance.fieldChoices);
    return response.data;
  },

  // Extract insurance data from OCR text via AI
  extractFromDocument: async (ocrText: string): Promise<ExtractedInsuranceData> => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.insurance.extractFromDocument,
      { ocr_text: ocrText },
      { timeout: 60000 }
    );
    return response.data.data;
  },

  // Extract insurance data from uploaded file (PDF/image) — server-side OCR + AI
  extractFromFile: async (file: File): Promise<ExtractedInsuranceData> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post(
      API_ENDPOINTS.insurance.extractFromFile,
      formData,
      { timeout: 120000, headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  // Get companies filtered by insurance type
  getCompanies: async (type?: string): Promise<InsuranceCompanyOption[]> => {
    const params = type ? { type } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.insurance.companies, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  // Generate AI summary for a policy
  generateSummary: async (id: number): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.insurance.generateSummary(id), {}, { timeout: 60000 });
    return response.data;
  },

  // Get cached summary
  getSummary: async (id: number): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.insurance.policySummary(id));
    return response.data;
  },

  // Get coverage insights & analytics
  getInsights: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.insurance.insights, { timeout: 60000 });
    return response.data;
  },
};

export default insuranceService;
